import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';
import { createSliceEntityAdapter, EntityState } from 'ngrx-slice/entity';

import {
  GetMerchantSitesResponse,
  MarketingDataViewModel,
  MerchantBasicDataViewModel,
  MerchantContactsViewModel,
  MerchantResponse,
  MerchantSearchDetailsFilter,
  MerchantSearchResponse,
  MerchantSiteMerchantTypeViewModel,
  MerchantSiteViewModel,
  PageableObject,
  ServedCustomerSegmentResponse,
  SiteSelectItemDto,
  SortObject,
} from '@app/api/marketplace/models';
import { ListMarketingDataViewModel } from '@app/api/marketplace/models/list-marketing-data-view-model';
import { AuthenticationSelectors } from '@authentication/store/authentication.slice';
import { ImageUploadRequest } from '@image-upload/interfaces/image-upload-request.interface';
import { ImageUploadResponse } from '@image-upload/interfaces/image-upload-response.interface';
import { ApiRoutes } from '@shared/constants/api-routes';
import {
  ApiErrorResponse,
  ApiErrorResponseWithId,
} from '@shared/interfaces/api-error-response';
import { FileUpload } from '@shared/interfaces/merchant-shared';
import { merchantRetailerTypes } from '@shared/interfaces/merchant-types';
import { PageRequest } from '@shared/interfaces/page-request.interface';
import { PageResponse } from '@shared/interfaces/page-response.interface';

export interface MerchantSearchRequest
  extends PageRequest,
    MerchantSearchDetailsFilter {}

export const initialDataState = {
  totalPages: 0,
  totalElements: 0,
  size: 0,
  content: [] as Array<MerchantSearchResponse>,
  number: 0,
  sort: {} as SortObject,
  pageable: {} as PageableObject,
  first: true,
  last: true,
  numberOfElements: 0,
  empty: true,
};

export interface State {
  // basic data
  basicDataLoadError: boolean;
  basicDataUpdating: boolean;
  basicData?: MerchantBasicDataViewModel;
  basicDataLoading: boolean;
  basicDataUpdateError?: ApiErrorResponse;
  activeMerchant?: MerchantResponse;

  // basic data - logo
  merchantLogoUpdating: boolean;
  merchantLogoUrl?: string;
  merchantLogoUpdateError?: ApiErrorResponse;

  // contact data
  contactsLoadError: boolean;
  contactsUpdating: boolean;
  contacts?: MerchantContactsViewModel;
  contactsUpdateError?: ApiErrorResponse;

  // sites - get
  sitesLoadError: boolean;
  sitesLoading: boolean;
  sitesLoaded: boolean;
  sites: EntityState<MerchantSiteViewModel>;
  sitesByMerchantId: EntityState<MerchantSiteViewModel>;
  siteId: string;
  siteSelectItems: Array<SiteSelectItemDto>;
  servedCustomerSegments: Array<ServedCustomerSegmentResponse>;

  // sites - edit
  siteLoading: boolean;
  siteEditError?: ApiErrorResponse;

  // sites - delete
  siteDeleting: boolean;
  siteDeleteError: boolean;

  // marketingData - get
  marketingDataLoadError: boolean;
  marketingDataLoading: boolean;
  marketingDataLoaded: boolean;
  marketingData: EntityState<MarketingDataViewModel>;

  // marketingData - edit
  marketingDataEditing: boolean;
  marketingDataEditError?: ApiErrorResponse;

  // marketingData - delete
  marketingDataDeleting: boolean;
  marketingDataDeleteError: boolean;

  // Merchant profile image - DELETE
  merchantProfileImageDeleteError: ApiErrorResponse | null;
  merchantProfileImageDeleting: boolean;

  // Merchant profile image - UPDATE
  merchantProfileImageUploadError: ApiErrorResponse | null;
  merchantProfileImageUploading: boolean;

  // Merchant profile image: GET
  merchantProfileimageGetError: ApiErrorResponse | null;
  merchantProfileImageUrl: string | null;
  merchantProfileImage: ImageUploadResponse | null;

  // partners
  partnersFilter: MerchantSearchRequest;
  partnersResponse: PageResponse<MerchantSearchResponse>;
  partnersResponseLoading: boolean;

  // Contract
  acceptContractLoading: boolean;
  acceptContractError: boolean;

  // operatingLicense
  operatingLicense: string;
  operatingLicenseUploadProgress: number;
  isOperatingLicenseUploading: boolean;
  hasOperatingLicenseError: boolean;
  operatingLicenseInStore?: File;
}

export const siteAdapter = createSliceEntityAdapter<MerchantSiteViewModel>({
  sortComparer: (s1, s2) => s1.name.localeCompare(s2.name),
  selectId: (s) => s.id,
});

export const marketingDataAdapter =
  createSliceEntityAdapter<MarketingDataViewModel>({
    selectId: (s) => s.id,
  });

export const initialState: State = {
  // basic data
  basicDataLoadError: false,
  basicDataUpdating: false,
  basicData: undefined,
  basicDataLoading: false,
  basicDataUpdateError: undefined,
  activeMerchant: undefined,
  // basic data - logo
  merchantLogoUpdating: false,
  merchantLogoUrl: undefined,
  merchantLogoUpdateError: undefined,
  // contacts
  contactsLoadError: false,
  contactsUpdating: false,
  contacts: undefined,
  contactsUpdateError: undefined,

  // sites - get
  sitesLoading: false,
  sitesLoaded: false,
  sitesLoadError: false,
  sites: siteAdapter.getInitialState(),
  sitesByMerchantId: siteAdapter.getInitialState(),
  siteId: '',
  siteSelectItems: [],
  servedCustomerSegments: [],
  // sites - edit
  siteLoading: false,
  siteEditError: undefined,
  // sites - delete
  siteDeleting: false,
  siteDeleteError: false,

  // marketingData - get
  marketingDataLoadError: false,
  marketingDataLoading: false,
  marketingDataLoaded: false,
  marketingData: marketingDataAdapter.getInitialState(),
  // marketingData - edit
  marketingDataEditing: false,
  marketingDataEditError: undefined,
  // marketingData - delete
  marketingDataDeleting: false,
  marketingDataDeleteError: false,

  // Merchant profile image - UPDATE
  merchantProfileImageDeleteError: null,
  merchantProfileImageDeleting: false,

  // Merchant profile image: POST
  merchantProfileImageUploadError: null,
  merchantProfileImageUploading: false,

  // Merchant profile image: GET
  merchantProfileImageUrl: null,
  merchantProfileimageGetError: null,
  merchantProfileImage: null,

  // partners
  partnersFilter: {
    page: 0,
    searchText: '',
    sort: ['companyName,asc'],
  },
  partnersResponse: initialDataState,
  partnersResponseLoading: false,

  // Contract
  acceptContractLoading: false,
  acceptContractError: false,

  // operatingLicense
  operatingLicense: '',
  operatingLicenseUploadProgress: 0,
  isOperatingLicenseUploading: false,
  hasOperatingLicenseError: false,
  operatingLicenseInStore: undefined,
};

export const {
  actions: MerchantActions,
  selectors,
  ...MerchantFeature
} = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    loadBasicData: {
      trigger: (state) => ({
        ...state,
        basicData: undefined,
        basicDataLoading: true,
      }),
      success: (
        state,
        action: PayloadAction<{
          merchantId: string;
          data: MerchantBasicDataViewModel;
        }>
      ): State => ({
        ...state,
        basicData: action.data,
        merchantLogoUrl: ApiRoutes.getMerchantLogo(action.merchantId),
        basicDataLoading: false,
        basicDataLoadError: false,
      }),
      failure: (state): State => ({
        ...state,
        basicDataLoading: false,
        basicDataLoadError: true,
      }),
    },
    loadBasicDataById: {
      trigger: (state) => ({
        ...state,
        basicDataLoading: true,
      }),
      success: (
        state,
        action: PayloadAction<{
          merchantId: string;
          data: MerchantBasicDataViewModel;
        }>
      ): State => ({
        ...state,
        basicData: action.data,
        merchantLogoUrl: ApiRoutes.getMerchantLogo(action.merchantId),
        basicDataLoading: false,
        basicDataLoadError: false,
      }),
      failure: (state): State => ({
        ...state,
        basicDataLoading: false,
        basicDataLoadError: true,
      }),
    },
    updateBasicData: {
      trigger: (
        state,
        _action: PayloadAction<{
          data: MerchantBasicDataViewModel;
          firstLogin: boolean;
        }>
      ): State => ({
        ...state,
        basicDataUpdating: true,
      }),
      success: (state): State => ({
        ...state,
        basicDataUpdating: false,
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>): State => ({
        ...state,
        basicDataUpdating: false,
        basicDataUpdateError: action,
      }),
    },
    // Telesales step 3
    updateBasicDataByTelesales: {
      trigger: (
        state,
        _action: PayloadAction<{
          data: MerchantBasicDataViewModel;
        }>
      ): State => ({
        ...state,
        basicDataUpdating: true,
      }),
      success: (state): State => ({
        ...state,
        basicDataUpdating: false,
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>): State => ({
        ...state,
        basicDataUpdating: false,
        basicDataUpdateError: action,
      }),
    },
    loadMerchantById: {
      trigger: (state, action: PayloadAction<{ id: string }>): State => ({
        ...state,
        basicDataLoading: true,
      }),
      success: (
        state,
        action: PayloadAction<{ data: MerchantResponse }>
      ): State => ({
        ...state,
        activeMerchant: action.data,
        basicDataLoading: false,
        basicDataLoadError: false,
      }),
      failure: (state): State => ({
        ...state,

        basicDataLoading: false,
        basicDataLoadError: true,
      }),
    },
    loadContacts: {
      trigger: () => {},
      success: (
        state,
        action: PayloadAction<MerchantContactsViewModel>
      ): State => ({
        ...state,
        contacts: action,
        contactsLoadError: false,
      }),
      failure: (state): State => ({
        ...state,
        contactsLoadError: true,
      }),
    },
    updateContacts: {
      trigger: (
        state,
        _action: PayloadAction<MerchantContactsViewModel>
      ): State => ({
        ...state,
        contactsUpdating: true,
      }),
      success: (
        state,
        action: PayloadAction<MerchantContactsViewModel>
      ): State => ({
        ...state,
        contacts: action,
        contactsUpdating: false,
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>): State => ({
        ...state,
        contactsUpdating: false,
        contactsUpdateError: action,
      }),
    },
    updateMerchantLogo: {
      trigger: (state, _action: PayloadAction<FileUpload>): State => ({
        ...state,
        merchantLogoUpdating: true,
      }),
      success: (state): State => ({
        ...state,
        merchantLogoUrl: `${state.merchantLogoUrl}?${Date.now()}`, // triggers a cache miss
        merchantLogoUpdating: false,
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>): State => ({
        ...state,
        merchantLogoUpdating: false,
        merchantLogoUpdateError: action,
      }),
    },
    deleteMerchantProfileImage: {
      trigger: (state, _: PayloadAction<{ id: string }>) => ({
        ...state,
        merchantProfileImageDeleting: true,
      }),
      success: (state) => ({
        ...state,
        merchantProfileImageDeleteError: null,
        merchantProfileImageDeleting: false,
        merchantProfileImageUrl: null,
        merchantProfileImage: null,
      }),
      failure: (state, action) => ({
        ...state,
        merchantProfileImageDeleteError: action,
        merchantProfileImageDeleting: false,
      }),
    },
    getMerchantProfileImage: {
      trigger: (state, _action: PayloadAction<{ id: string }>) => ({
        ...state,
      }),
      success: (state, action: PayloadAction<ImageUploadResponse>) => ({
        ...state,
        merchantProfileImageUrl: action.url,
      }),
      failure: (state, action) => ({
        ...state,
        merchantProfileimageGetError: action,
      }),
    },
    uploadMerchantProfileImage: {
      trigger: (state, _action: PayloadAction<ImageUploadRequest>): State => ({
        ...state,
        merchantProfileImageUploading: true,
      }),
      success: (
        state,
        action: PayloadAction<ImageUploadResponse[]>
      ): State => ({
        ...state,
        merchantProfileImageUrl: action[0].url,
        merchantProfileImageUploading: false,
        merchantProfileImage: action[0],
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>): State => ({
        ...state,
        merchantProfileImageUploading: false,
        merchantProfileImageUploadError: action,
      }),
    },
    loadSites: {
      trigger: (state) => {
        state.sitesLoading = true;
      },
      success: (state, action: PayloadAction<GetMerchantSitesResponse>) => {
        siteAdapter.upsertMany(state.sites, action.sites);
        state.sitesLoading = false;
        state.sitesLoaded = true;
        state.sitesLoadError = false;
      },
      failure: (state) => {
        state.sitesLoading = false;
        state.sitesLoadError = true;
      },
    },
    loadSitesByMerchantId: {
      trigger: (state, action: PayloadAction<{ merchantId: string }>) => {
        state.sitesLoading = true;
      },
      success: (state, action: PayloadAction<GetMerchantSitesResponse>) => {
        siteAdapter.upsertMany(state.sitesByMerchantId, action.sites);
        state.sitesLoading = false;
        state.sitesLoaded = true;
        state.sitesLoadError = false;
      },
      failure: (state) => {
        state.sitesLoading = false;
        state.sitesLoadError = true;
      },
    },
    setSiteId: {
      trigger: (state, action: PayloadAction<{ siteId: string }>) => {
        state.siteId = action.siteId;
      },
      success: () => {},
    },
    resetSites: (state) => {
      siteAdapter.removeAll(state.sitesByMerchantId);
    },
    loadSiteSelectItems: {
      trigger: () => {},
      success: (
        state,
        action: PayloadAction<{ response: Array<SiteSelectItemDto> }>
      ) => {
        state.siteSelectItems = action.response;
      },
      failure: () => {},
    },
    createSite: {
      trigger: (
        state,
        _action: PayloadAction<{ site: MerchantSiteViewModel }>
      ) => {
        state.siteLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: MerchantSiteViewModel }>
      ) => {
        siteAdapter.addOne(state.sites, action.response);
        state.siteLoading = false;
        state.siteEditError = undefined;
      },
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.siteLoading = false;
        state.siteEditError = action;
      },
    },
    // Telesales step 4
    createSiteByTelesales: {
      trigger: (
        state,
        _action: PayloadAction<{
          site: MerchantSiteViewModel;
        }>
      ) => {
        state.siteLoading = true;
      },
      success: () => {},
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.siteLoading = false;
        state.siteEditError = action;
      },
    },
    editSite: {
      trigger: (
        state,
        _action: PayloadAction<{ site: MerchantSiteViewModel }>
      ) => {
        state.siteLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: MerchantSiteViewModel }>
      ) => {
        siteAdapter.upsertOne(state.sites, action.response);
        state.siteLoading = false;
        state.siteEditError = undefined;
      },
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.siteLoading = false;
        state.siteEditError = action;
      },
    },
    loadServedCustomerSegments: {
      trigger: (
        _state,
        _action: PayloadAction<{
          merchantTypes: Array<MerchantSiteMerchantTypeViewModel>;
        }>
      ) => {},
      success: (
        state,
        action: PayloadAction<{
          segments: Array<ServedCustomerSegmentResponse>;
        }>
      ) => {
        state.servedCustomerSegments = action.segments;
      },
    },
    deleteSite: {
      trigger: (state, _action: PayloadAction<{ id: string }>) => {
        state.siteDeleting = true;
      },
      success: (state, action: PayloadAction<{ id: string }>) => {
        siteAdapter.removeOne(state.sites, action.id);
        state.siteDeleting = false;
        state.siteDeleteError = false;
      },
      failure: (state) => {
        state.siteDeleting = false;
        state.siteDeleteError = true;
      },
    },
    loadMarketingData: {
      trigger: (state) => {
        state.marketingDataLoading = true;
      },
      success: (state, action: PayloadAction<ListMarketingDataViewModel>) => {
        marketingDataAdapter.upsertMany(
          state.marketingData,
          action.marketingData ?? {}
        );
        state.marketingDataLoading = false;
        state.marketingDataLoaded = true;
        state.marketingDataLoadError = false;
      },
      failure: (state) => {
        state.marketingDataLoading = false;
        state.marketingDataLoadError = true;
      },
    },
    editMarketingData: {
      trigger: (state, _action: PayloadAction<MarketingDataViewModel>) => {
        state.marketingDataEditing = true;
      },
      success: (state, action: PayloadAction<MarketingDataViewModel>) => {
        marketingDataAdapter.upsertOne(state.marketingData, action);
        state.marketingDataEditing = false;
        state.marketingDataEditError = undefined;
      },
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.marketingDataEditing = false;
        state.marketingDataEditError = action;
      },
    },
    deleteMarketingData: {
      trigger: (state, _action: PayloadAction<{ id: string }>) => {
        state.marketingDataDeleting = true;
      },
      success: (state, action: PayloadAction<{ id: string }>) => {
        marketingDataAdapter.removeOne(state.marketingData, action.id);
        state.marketingDataDeleting = false;
        state.marketingDataDeleteError = false;
      },
      failure: (state) => {
        state.marketingDataDeleting = false;
        state.marketingDataDeleteError = true;
      },
    },
    updatePartnersFilter: {
      trigger: (
        state,
        action: PayloadAction<{ filter: MerchantSearchRequest }>
      ) => {
        if (
          !action.filter?.hasOwnProperty('page') &&
          !action.filter?.hasOwnProperty('sort')
        ) {
          state.partnersFilter = {
            ...state.partnersFilter,
            page: 0,
          };
        }
        state.partnersFilter = {
          ...state.partnersFilter,
          ...action.filter,
          sort: action.filter.sort?.length
            ? action.filter.sort
            : state.partnersFilter.sort,
        };
      },
      success: () => {},
    },
    searchPartners: {
      trigger: (state) => {
        state.partnersResponse = initialDataState;
        state.partnersResponseLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          response: PageResponse<MerchantSearchResponse>;
        }>
      ) => {
        state.partnersResponse = action.response;
        state.partnersResponseLoading = false;
      },
      failure: (state) => ({
        ...state,
        partnersResponseLoading: false,
      }),
    },
    acceptContract: {
      trigger: (
        state,
        action: PayloadAction<{ request: { hash: string; merchantId: string } }>
      ) => ({
        ...state,
        acceptContractLoading: true,
      }),
      success: (state) => ({
        ...state,
        acceptContractLoading: false,
        acceptContractError: false,
      }),
      failure: (state) => ({
        ...state,
        acceptContractLoading: false,
        acceptContractError: true,
      }),
    },
    resetSearchPartners: {
      trigger: (state) => {
        state.partnersResponse = initialDataState;
        state.partnersFilter = {
          page: 0,
          searchText: '',
          sort: ['companyName,asc'],
        };
      },
      success: () => {},
    },
    loadOperatingLicense: {
      trigger: (
        state,
        action: PayloadAction<{ merchantId: string; siteId: string }>
      ) => ({ ...state, operatingLicense: '' }),
      success: (state, action: PayloadAction<{ file: string | null }>) => ({
        ...state,
        operatingLicense: action.file,
      }),
    },
    uploadOperatingLicense: {
      trigger: (
        state,
        _: PayloadAction<{
          file: File;
          merchantId: string;
          siteId: string;
          notOpenSnackBar?: boolean;
        }>
      ): State => ({
        ...state,
        isOperatingLicenseUploading: true,
      }),
      progress: (
        state: State,
        action: PayloadAction<{ progress: number }>
      ): State => ({
        ...state,
        operatingLicenseUploadProgress: action.progress,
      }),
      success: (state) => {
        state.hasOperatingLicenseError = false;
        state.isOperatingLicenseUploading = false;
        state.operatingLicenseUploadProgress = 0;
      },
      failure: (state): State => ({
        ...state,
        hasOperatingLicenseError: true,
        isOperatingLicenseUploading: false,
        operatingLicenseUploadProgress: 0,
      }),
    },
    uploadOperatingLicenseToStore: {
      trigger: (
        state,
        action?: PayloadAction<{
          file: File;
          merchantId: string;
        }>
      ): State => ({
        ...state,
        operatingLicenseInStore: action?.file,
      }),
      success: () => {},
    },
    reset: (_) => initialState,
    resetMerchantImage: (state) => {
      state.merchantProfileImage = null;
      state.merchantProfileImageUrl = null;
    },
  },
});

export const selectSiteList = createSelector(selectors.selectSites, (sites) =>
  Object.values(sites.entities)
);

export const MerchantSelectors = {
  ...selectors,
  isBasicDataLoading: createSelector(
    selectors.selectBasicDataLoading,
    (loading) => loading
  ),
  isBasicDataLoaded: createSelector(
    selectors.selectBasicData,
    (data) => !!data
  ),
  basicDataAddress: createSelector(
    selectors.selectBasicData,
    (data) => data?.businessAddress
  ),
  isBasicDataPackingMaterialNone: createSelector(
    selectors.selectBasicData,
    (data) => data?.packingMaterial === 'NONE'
  ),
  isContactsLoading: createSelector(
    selectors.selectContacts,
    selectors.selectContactsLoadError,
    (data, error) => !(data || error)
  ),
  isContactsLoaded: createSelector(selectors.selectContacts, (data) => !!data),
  selectSiteList,
  selectSiteListByMerchantId: createSelector(
    selectors.selectSitesByMerchantId,
    (sites) => Object.values(sites.entities)
  ),
  selectSite: (id?: string) =>
    createSelector(selectors.selectSites, (sites) =>
      id ? sites.entities[id] : undefined
    ),
  selectSiteByMerchantId: createSelector(
    selectors.selectSitesByMerchantId,
    selectors.selectSiteId,
    (sites, siteId) => (siteId ? sites.entities[siteId] : null)
  ),
  selectIsSiteSelected: createSelector(
    selectSiteList,
    AuthenticationSelectors.selectUserInfo,
    (sites, sess) => {
      const selectedSiteId = sess.attributes?.['custom:selectedSiteId'];
      const selectedSite = sites.find((s) => s.id === selectedSiteId);
      return !!selectedSite;
    }
  ),
  selectMarketingDataList: createSelector(
    selectors.selectMarketingData,
    (marketingData) => Object.values(marketingData.entities)
  ),
  selectSingleMarketingData: (id?: string) =>
    createSelector(selectors.selectMarketingData, (marketingData) =>
      id ? marketingData.entities[id] : undefined
    ),
  selectRequiredMerchantDataFilled: createSelector(
    selectors.selectBasicData,
    (data): boolean => data?.requiredMerchantDataFilled ?? false
  ),
  selectVatExempt: createSelector(
    selectors.selectBasicData,
    (data): boolean => data?.vatExempt ?? false
  ),
  selectMerchantType: createSelector(
    selectors.selectBasicData,
    (data): string => data?.highestMerchantType ?? ''
  ),
  selectIsImporter: createSelector(
    selectors.selectSites,
    (sites): boolean =>
      !!Object.values(sites.entities).find((site) =>
        site.merchantTypes.find(
          (elem) =>
            elem.merchantType &&
            !['WHOLESALER_AS_IS', ...merchantRetailerTypes].includes(
              elem.merchantType
            )
        )
      )
  ),
};
